import React, { createContext, useContext, useState, useCallback } from 'react';

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const NotificationContext = createContext(undefined);

// ============================================================================
// CUSTOM HOOK
// ============================================================================

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within NotificationProvider');
    }
    return context;
};

// ============================================================================
// NOTIFICATION TOAST COMPONENT
// ============================================================================

const NotificationToast = ({ notification, onClose }) => {
    const { message, type } = notification;
    
    const colors = {
        success: { bg: '#10b981', icon: '✓' },
        error: { bg: '#ef4444', icon: '✕' },
        info: { bg: '#3b82f6', icon: 'ℹ' },
        warning: { bg: '#f59e0b', icon: '⚠' }
    };
    
    const config = colors[type] || colors.info;
    
    return (
        <div style={{
            backgroundColor: 'white',
            color: '#111827',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            boxSizing: 'border-box',
            animation: 'slideInRight 0.3s ease-out',
            border: `2px solid ${config.bg}`
        }}>
            <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: config.bg,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: 'bold',
                flexShrink: 0
            }}>
                {config.icon}
            </div>
            
            <div style={{ flex: 1, fontSize: '14px', fontWeight: '500' }}>
                {message}
            </div>
            
            <button
                onClick={onClose}
                style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: '0 4px',
                    lineHeight: 1,
                    transition: 'color 0.2s',
                    flexShrink: 0
                }}
                onMouseEnter={(e) => e.target.style.color = '#111827'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
            >
                ×
            </button>
        </div>
    );
};

// ============================================================================
// CONFIRMATION DIALOG COMPONENT
// ============================================================================

const ConfirmationDialog = ({ dialog }) => {
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            animation: 'fadeIn 0.2s ease-out'
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '28px',
                maxWidth: '450px',
                width: '90%',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                animation: 'scaleIn 0.2s ease-out'
            }}>
                <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '16px',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap'
                }}>
                    {dialog.message}
                </div>
                
                <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    marginTop: '24px'
                }}>
                    <button
                        onClick={dialog.onCancel}
                        style={{
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                            backgroundColor: '#f3f4f6',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    >
                        {dialog.cancelText}
                    </button>
                    
                    <button
                        onClick={dialog.onConfirm}
                        style={{
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: 'white',
                            backgroundColor: '#3b82f6',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                    >
                        {dialog.confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [confirmDialog, setConfirmDialog] = useState(null);

    /**
     * Show a toast notification
     * @param {string} message - Message to display
     * @param {string} type - 'success' | 'error' | 'info' | 'warning'
     * @param {number} duration - Duration in ms (0 = permanent)
     */
    const showNotification = useCallback((message, type = 'info', duration = 3000) => {
        const id = Date.now() + Math.random();
        const notification = { id, message, type };
        
        setNotifications(prev => [...prev, notification]);
        
        if (duration > 0) {
            setTimeout(() => {
                setNotifications(prev => prev.filter(n => n.id !== id));
            }, duration);
        }
    }, []);

    /**
     * Show a confirmation dialog
     * @param {string} message - Message to display
     * @param {Function} onConfirm - Callback when user clicks confirm
     * @param {Function} onCancel - Callback when user clicks cancel (optional)
     * @param {Object} options - Additional options { confirmText, cancelText }
     */
    const showConfirm = useCallback((message, onConfirm, onCancel = null, options = {}) => {
        setConfirmDialog({
            message,
            onConfirm: () => {
                onConfirm();
                setConfirmDialog(null);
            },
            onCancel: () => {
                if (onCancel) onCancel();
                setConfirmDialog(null);
            },
            confirmText: options.confirmText || 'OK',
            cancelText: options.cancelText || 'Abbrechen'
        });
    }, []);

    const hideNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    return (
        <NotificationContext.Provider value={{ showNotification, showConfirm, hideNotification }}>
            {children}
            
            {/* Toast Notifications */}
            <div
                className="notification-toast-container"
                style={{
                    position: 'fixed',
                    top: '20px',
                    right: '16px',
                    zIndex: 99999,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    width: 'min(400px, calc(100vw - 32px))'
                }}
            >
                {notifications.map(notification => (
                    <NotificationToast
                        key={notification.id}
                        notification={notification}
                        onClose={() => hideNotification(notification.id)}
                    />
                ))}
            </div>

            {/* Confirmation Dialog */}
            {confirmDialog && (
                <ConfirmationDialog dialog={confirmDialog} />
            )}
        </NotificationContext.Provider>
    );
};

// ============================================================================
// ADD CSS ANIMATIONS
// ============================================================================

if (typeof document !== 'undefined') {
    const styleId = 'notification-animations';
    
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
            @keyframes slideInRight {
                from {
                    transform: translateX(100%);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }
            
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            
            @keyframes scaleIn {
                from {
                    transform: scale(0.95);
                    opacity: 0;
                }
                to {
                    transform: scale(1);
                    opacity: 1;
                }
            }

            @media (max-width: 480px) {
                .notification-toast-container {
                    top: 12px;
                    right: 12px;
                    width: calc(100vw - 24px) !important;
                }
            }
        `;
        document.head.appendChild(style);
    }
}

export default NotificationProvider;